// src/App.tsx

import React, { useEffect, useState } from 'react';
import { createBrowserRouter, Link } from 'react-router-dom';
import {
  Menu,
  X,
  Shield,
  Mail,
  Globe,
  Network,
  Box,
  Code2,
  MonitorPlay,
  Building,
  Terminal,
  Rocket,
  Zap,
} from 'lucide-react';

import logoIcon from './logo.svg';


function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [topoInstance, setTopoInstance] = useState<any>(null);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (id: string) => {
    const [hash, queryString] = id.split('?');

    // Scroll to the section
    if (hash === "#" || hash === "") {
      // Scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    // Update the URL with the query string
    const newUrl = queryString ? `${hash}?${queryString}` : hash;
    window.history.pushState({}, '', newUrl);

    // Manually trigger the `ApplicationForm` to react to the URL change
    window.dispatchEvent(new Event('popstate'));
  };

  const topologyData = {
    nodes: [
      { id: 'router1', iconName: 'network.router', label: 'R1', layer: 10, customAttributes: {name: 'router1'}},
      { id: 'router2', iconName: 'network.router', label: 'R2', layer: 20, customAttributes: {name: 'router2'}}, 
      { id: 'router3', iconName: 'network.router', label: 'R3', layer: 20, customAttributes: {name: 'router2'}},
    ],
    edges: [
      {
        source: 'router1',
        target: 'router2',
        sourceInterface: { id: 'Gi0/0', labelText: 'Gi0/0' },
        targetInterface: { id: 'Gi0/0', labelText: 'Gi0/0' }
      },
      {
        source: 'router1',
        target: 'router3',
        sourceInterface: { id: 'Gi0/1', labelText: 'Gi0/1' },
        targetInterface: { id: 'Gi0/1', labelText: 'Gi0/1' }
      },
      {
        source: 'router2',
        target: 'router3',
        sourceInterface: { id: 'Gi0/1', labelText: 'Gi0/1' },
        targetInterface: { id: 'Gi0/0', labelText: 'Gi0/0' }
      },
    ],
    groups: [
      {
        id: 'routers',
        name: 'Routers',
        memberNodes: ['router1', 'router2', 'router3']
      }
    ]
  }

  const topologyConfig = {
    layouts: {
      defaultLayout: 'layered', 
      layered: {
          orientation: 'vertical',
          order: 'ascending',
          sortOrder: ['group.id', 'id'],
          minNodeDistance: 85,
          minLayerDistance: 50,
          maxNodeDistance: 10000,
          maxLayerDistance: 10000,
      },
    },
    theme: 'network-blue',
    groupHighlighting: true,
    adaptiveScaling: {
      enabled: true,
      scalingFactor: 0.8,
    },
    minZoom: 0.125,
    maxZoom: 3,
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.split('?')[0];
      if (hash) {
        scrollToSection(hash);
      }
    };
  
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Handle initial hash
  
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#050614] via-[#0a1425] to-[#112340]">
      <nav className="flex items-center w-full bg-white/10 backdrop-blur-lg shadow-lg z-[9999] border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left section: Logo + Navigation Links */}
            <div className="flex items-center space-x-8">
              {/* Logo */}
              <a
                href="#"
                className="flex items-center group"
                onClick={(e) => {
                  e.preventDefault();
                  const logoImg = e.currentTarget.querySelector('img');
                  
                  // Check if animation is already in progress
                  if (logoImg.classList.contains('animate-spin-once')) {
                    return; // Exit early if animation is active
                  }
                  
                  scrollToSection('#');
                  // Add rotation class to trigger animation
                  logoImg.classList.add('animate-spin-once');
                  
                  // Remove class after animation completes to allow retriggering
                  setTimeout(() => {
                    logoImg.classList.remove('animate-spin-once');
                  }, 1000); // Match duration with animation
                }}
              >
                <div className="relative">
                  <img
                    src={logoIcon}
                    alt="Logo"
                    className="w-10 h-10 transition-all duration-300 ease-in-out transform group-hover:scale-110 group-hover:opacity-100"
                    draggable="false"
                  />
                  <style>{`
                    @keyframes spinOnce {
                      from { transform: rotate(0deg) scale(1.1); }
                      to { transform: rotate(360deg) scale(1.1); }
                    }
                    .animate-spin-once {
                      animation: spinOnce 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
                      pointer-events: none; /* Prevent interaction during animation */
                    }
                  `}</style>
                </div>
                <span className="ml-2 text-xl font-bold text-white" draggable="false">
                  topoSphere
                </span>
              </a>

              {/* Desktop Navigation Links */}
              <div className="hidden md:flex items-center space-x-8">
              </div>
            </div>

            {/* Right section: Action Buttons + Mobile Menu */}
            <div className="flex items-center">
              {/* Desktop Action Buttons */}
              <div className="hidden md:flex items-center space-x-4">
              </div>

              {/* Mobile Menu Button */}
              <button onClick={toggleMenu} className="md:hidden ml-4">
                {isMenuOpen ? (
                  <X className="h-6 w-6 text-white" />
                ) : (
                  <Menu className="h-6 w-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-x-0 top-16 bg-[#050614]/95 backdrop-blur-md z-[9999] border-b border-white/10">
            <div className="px-4 py-3 space-y-3">
            </div>
          </div>
        )}
      </nav>

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background layers system */}
        <div className="absolute inset-0">
          {/* Base gradient overlay for consistent dark background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#030408]/75 via-[#060b14]/80 to-[#0d1a30]/85" />
          
          {/* Network pattern animation layer */}
          <div className="absolute inset-0">
            <div 
              className="absolute inset-0 bg-[url('/src/earth-connections.webp')] bg-cover bg-center animate-pulse" 
              style={{
                animationDuration: '8s',
                opacity: '0.07',
                animation: 'pulse 12s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }}
            />
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 0.05; }
                50% { opacity: 0.15; }
              }
            `}</style>
          </div>
        </div>

        {/* Main content container */}
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center min-h-screen text-center">
            {/* Content wrapper with spacing */}
            <div className="space-y-10">
              {/* GitHub stats banner with adaptive top margin */}
              <div className="transform hover:scale-[1.02] transition-transform mt-12 sm:mt-0">
                <div className="px-4 py-2 rounded-full bg-blue-500/15 backdrop-blur-sm border border-blue-500/20 shadow-lg inline-block">
                  <p className="text-xs sm:text-sm text-blue-300 font-medium whitespace-normal max-w-[42ch] sm:max-w-none">
                    Built on the success of NextBox-UI, a top NetBox plugin trusted by hundreds of companies. 580+ GitHub stars.
                  </p>
                </div>
              </div>

              {/* Main heading */}
              <h1 className="relative">
                <span className="relative block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight antialiased">
                  <span className="bg-gradient-to-r from-white via-white to-blue-100 bg-clip-text text-transparent [text-shadow:_0_1px_0_rgb(255_255_255_/_10%)]">
                    Network Visualization That Just&nbsp;Works
                  </span>
                </span>
              </h1>

              {/* Subheadline with glow effect */}
              <div className="relative max-w-2xl mx-auto">
                <div className="absolute inset-0 bg-blue-500/5 blur-xl rounded-full"></div>
                <p className="relative text-lg sm:text-xl text-white/90 leading-relaxed font-semibold">
                  <span className="bg-gradient-to-r from-white/90 via-white/95 to-white/90 bg-clip-text text-transparent">
                    Developers save time with network-specific features. Users enjoy smooth, interactive network maps.
                  </span>
                </p>
              </div>

              {/* Coming Soon badge */}
              <div className="inline-flex items-center">
                <div className="px-6 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-blue-400/10 backdrop-blur-md">
                  <p className="text-sm font-medium text-blue-200 tracking-[0.2em] relative">
                    COMING SOON
                  </p>
                </div>
              </div>

              {/* CTA buttons - Fixed desktop layout and maintained mobile responsiveness */}
              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-lg mx-auto">
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer id="contact" className="bg-[#050614] text-white py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Copyright Bar */}
          <div className="border-t border-white/10 mt-12 pt-8 text-center">
            <p className="text-white/60">
              &copy; 2024-{new Date().getFullYear()} topoSphere. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
